import React, { useState, useEffect } from 'react';
import {
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Divider,
  Paper,
  Grid,
  Tooltip,
  Chip,
  Card,
  CardContent,
  CardActions,
  Popover
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Timeline as TimelineIcon,
  Save as SaveIcon,
  Flag as FlagIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import ProModal from '../../common/ProModal';

const DEFAULT_SETTINGS = {
  startYear: 0,
  endYear: 1000,
  yearLabel: 'CE',
  displayYears: true
};

// Preset colors for eras
const ERA_COLORS = [
  '#1e88e5', '#43a047', '#e53935', '#8e24aa', '#fb8c00',
  '#00acc1', '#3949ab', '#d81b60', '#7cb342', '#6d4c41',
  '#5e35b1', '#039be5', '#c0ca33', '#f4511e', '#00897b'
];

const TimelineEraManager = ({ 
  open, 
  onClose, 
  timeline = { events: [], eras: [], settings: DEFAULT_SETTINGS }, 
  onSave,
  onAddEra,
  onEditEra,
  onDeleteEra
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  
  // Theme colors
  const accentColor = isDarkMode ? '#8ec5ff' : '#1e88e5';
  const accentBg = isDarkMode ? 'rgba(142, 197, 255, 0.15)' : 'rgba(30, 136, 229, 0.08)';
  const accentBorder = isDarkMode ? 'rgba(142, 197, 255, 0.3)' : 'rgba(30, 136, 229, 0.2)';
  const cardBg = isDarkMode ? 'rgba(20, 30, 48, 0.95)' : '#ffffff';
  const surfaceBg = isDarkMode ? 'rgba(10, 20, 35, 0.9)' : '#f5f9ff';

  // Ensure timeline has all required properties
  const completeTimeline = {
    events: timeline.events || [],
    eras: timeline.eras || [],
    settings: timeline.settings || DEFAULT_SETTINGS
  };

  // State
  const [eras, setEras] = useState([]);
  const [editingEra, setEditingEra] = useState(null);
  const [colorAnchor, setColorAnchor] = useState(null);
  const [errors, setErrors] = useState({});

  // Initialize eras from timeline
  useEffect(() => {
    if (open) {
      setEras(Array.isArray(timeline?.eras) ? [...timeline.eras] : []);
      setEditingEra(null);
      setErrors({});
    }
  }, [open, timeline?.eras]);

  // Create new era
  const handleAddEra = () => {
    const newEra = {
      id: `era_${Date.now()}`,
      name: '',
      description: '',
      color: ERA_COLORS[eras.length % ERA_COLORS.length],
      startYear: completeTimeline.settings.startYear,
      endYear: completeTimeline.settings.endYear
    };
    setEditingEra(newEra);
    setErrors({});
  };

  // Edit existing era
  const handleEditEra = (era) => {
    setEditingEra({ ...era });
    setErrors({});
  };

  // Delete era
  const handleDeleteEra = (eraId) => {
    const hasEvents = completeTimeline.events.some(e => e.era === eraId);
    if (hasEvents && !window.confirm('This era has events. Delete anyway? Events will be unassigned.')) {
      return;
    }
    
    const updatedEras = eras.filter(e => e.id !== eraId);
    setEras(updatedEras);
    
    if (editingEra?.id === eraId) {
      setEditingEra(null);
    }
    
    // Immediately save deletion
    if (onDeleteEra) {
      onDeleteEra(eraId);
    }
  };

  // Handle form field changes
  const handleFieldChange = (field, value) => {
    setEditingEra(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Validate era
  const validateEra = () => {
    const newErrors = {};
    if (!editingEra?.name?.trim()) {
      newErrors.name = 'Name is required';
    }
    if (editingEra?.startYear === undefined || editingEra?.startYear === '') {
      newErrors.startYear = 'Required';
    }
    if (editingEra?.endYear === undefined || editingEra?.endYear === '') {
      newErrors.endYear = 'Required';
    }
    if (Number(editingEra?.startYear) > Number(editingEra?.endYear)) {
      newErrors.endYear = 'Must be after start';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Save current era
  const handleSaveEra = () => {
    if (!validateEra()) return;

    const eraToSave = { ...editingEra };
    const isNew = !eras.some(e => e.id === eraToSave.id);
    
    let updatedEras;
    if (isNew) {
      updatedEras = [...eras, eraToSave];
      if (onAddEra) onAddEra(eraToSave);
    } else {
      updatedEras = eras.map(e => e.id === eraToSave.id ? eraToSave : e);
      if (onEditEra) onEditEra(eraToSave);
    }
    
    setEras(updatedEras);
    setEditingEra(null);
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingEra(null);
    setErrors({});
  };

  // Save all and close
  const handleSaveAll = () => {
    if (onSave) {
      onSave({
        ...completeTimeline,
        eras: eras
      });
    }
    onClose();
  };

  // Color picker handlers
  const handleColorClick = (event) => {
    setColorAnchor(event.currentTarget);
  };

  const handleColorClose = () => {
    setColorAnchor(null);
  };

  const handleColorSelect = (color) => {
    handleFieldChange('color', color);
    handleColorClose();
  };

  return (
    <ProModal
      open={open}
      onClose={onClose}
      title="Manage Timeline Eras"
      eyebrow="TIMELINE"
      icon={<TimelineIcon />}
      maxWidth="md"
      actions={(
        <>
          <Button 
            onClick={onClose} 
            color="inherit" 
            variant="outlined" 
            startIcon={<CloseIcon />} 
            sx={{ borderRadius: 1, textTransform: 'none', mr: 1 }}
          >
            Close
          </Button>
          <Button 
            onClick={handleSaveAll} 
            variant="contained"
            startIcon={<SaveIcon />}
            sx={{
              borderRadius: 1,
              textTransform: 'none',
              background: `linear-gradient(135deg, ${accentColor}, #1565c0)`,
              '&:hover': {
                background: `linear-gradient(135deg, #1565c0, #0d47a1)`
              }
            }}
          >
            Save & Close
          </Button>
        </>
      )}
      contentProps={{ sx: { p: 0 } }}
    >
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, minHeight: 450 }}>
        {/* Left Panel - Era List */}
        <Box sx={{ 
          width: { xs: '100%', md: 280 }, 
          borderRight: { md: `1px solid ${accentBorder}` },
          borderBottom: { xs: `1px solid ${accentBorder}`, md: 'none' },
          bgcolor: surfaceBg,
          p: 2
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: accentColor }}>
              ERAS ({eras.length})
            </Typography>
            <Tooltip title="Add New Era">
              <IconButton 
                size="small" 
                onClick={handleAddEra}
                disabled={!!editingEra}
                sx={{ 
                  bgcolor: accentBg, 
                  color: accentColor,
                  '&:hover': { bgcolor: accentColor, color: '#fff' }
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {eras.length === 0 ? (
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                textAlign: 'center', 
                bgcolor: 'transparent',
                border: `1px dashed ${accentBorder}`,
                borderRadius: 2
              }}
            >
              <FlagIcon sx={{ fontSize: 40, color: accentColor, opacity: 0.5, mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                No eras yet. Click + to create your first era.
              </Typography>
            </Paper>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {eras.map((era) => (
                <Paper
                  key={era.id}
                  elevation={0}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: `1px solid ${editingEra?.id === era.id ? accentColor : accentBorder}`,
                    bgcolor: editingEra?.id === era.id ? accentBg : cardBg,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: accentColor,
                      bgcolor: accentBg
                    }
                  }}
                  onClick={() => !editingEra && handleEditEra(era)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        bgcolor: era.color,
                        flexShrink: 0,
                        border: '2px solid rgba(255,255,255,0.3)'
                      }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                        {era.name || 'Unnamed Era'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {era.startYear} – {era.endYear}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteEra(era.id);
                      }}
                      disabled={!!editingEra}
                      sx={{ 
                        opacity: 0.6, 
                        '&:hover': { opacity: 1, color: 'error.main' } 
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </Box>

        {/* Right Panel - Edit Form */}
        <Box sx={{ flex: 1, p: 3 }}>
          {editingEra ? (
            <Box>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: accentColor }}>
                {eras.some(e => e.id === editingEra.id) ? 'Edit Era' : 'New Era'}
              </Typography>

              <Grid container spacing={2}>
                {/* Era Name */}
                <Grid item xs={12}>
                  <TextField
                    autoFocus
                    label="Era Name"
                    fullWidth
                    value={editingEra.name || ''}
                    onChange={(e) => handleFieldChange('name', e.target.value)}
                    error={!!errors.name}
                    helperText={errors.name}
                    placeholder="e.g., The Golden Age, The Dark Times"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>

                {/* Description */}
                <Grid item xs={12}>
                  <TextField
                    label="Description"
                    fullWidth
                    multiline
                    rows={2}
                    value={editingEra.description || ''}
                    onChange={(e) => handleFieldChange('description', e.target.value)}
                    placeholder="Brief description of this era..."
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>

                {/* Start Year */}
                <Grid item xs={6}>
                  <TextField
                    label="Start Year"
                    type="number"
                    fullWidth
                    value={editingEra.startYear ?? ''}
                    onChange={(e) => handleFieldChange('startYear', e.target.value ? Number(e.target.value) : '')}
                    error={!!errors.startYear}
                    helperText={errors.startYear}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>

                {/* End Year */}
                <Grid item xs={6}>
                  <TextField
                    label="End Year"
                    type="number"
                    fullWidth
                    value={editingEra.endYear ?? ''}
                    onChange={(e) => handleFieldChange('endYear', e.target.value ? Number(e.target.value) : '')}
                    error={!!errors.endYear}
                    helperText={errors.endYear}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>

                {/* Color Picker */}
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                    Era Color
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      onClick={handleColorClick}
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        bgcolor: editingEra.color || '#1e88e5',
                        cursor: 'pointer',
                        border: `2px solid ${accentBorder}`,
                        transition: 'transform 0.2s',
                        '&:hover': { transform: 'scale(1.1)' }
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Click to change color
                    </Typography>
                  </Box>
                  
                  <Popover
                    open={Boolean(colorAnchor)}
                    anchorEl={colorAnchor}
                    onClose={handleColorClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  >
                    <Box sx={{ p: 2, display: 'flex', flexWrap: 'wrap', gap: 1, maxWidth: 200 }}>
                      {ERA_COLORS.map((color) => (
                        <Box
                          key={color}
                          onClick={() => handleColorSelect(color)}
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 1,
                            bgcolor: color,
                            cursor: 'pointer',
                            border: editingEra.color === color ? '3px solid #fff' : '2px solid transparent',
                            boxShadow: editingEra.color === color ? `0 0 0 2px ${color}` : 'none',
                            transition: 'transform 0.2s',
                            '&:hover': { transform: 'scale(1.15)' }
                          }}
                        />
                      ))}
                    </Box>
                  </Popover>
                </Grid>

                {/* Action Buttons */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      onClick={handleCancelEdit}
                      sx={{ borderRadius: 2, textTransform: 'none' }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleSaveEra}
                      startIcon={<SaveIcon />}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        background: `linear-gradient(135deg, ${accentColor}, #1565c0)`,
                        '&:hover': {
                          background: `linear-gradient(135deg, #1565c0, #0d47a1)`
                        }
                      }}
                    >
                      {eras.some(e => e.id === editingEra.id) ? 'Update Era' : 'Create Era'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          ) : (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              minHeight: 300,
              color: 'text.secondary'
            }}>
              <TimelineIcon sx={{ fontSize: 60, opacity: 0.3, mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 1 }}>
                Select or Create an Era
              </Typography>
              <Typography variant="body2" sx={{ mb: 3, textAlign: 'center', maxWidth: 300 }}>
                Eras help organize your timeline into distinct periods. Click an era to edit it, or create a new one.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddEra}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  background: `linear-gradient(135deg, ${accentColor}, #1565c0)`
                }}
              >
                Create New Era
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </ProModal>
  );
};

export default TimelineEraManager;
